import { Button, Typography } from 'antd';
import { Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { tx } from '../../i18n/translate.js';

interface EmptyStateProps {
  /** 主标题 */
  title?: string;
  /** 描述（多行可选）*/
  description?: ReactNode;
  /** 自定义图标（来自 lucide-react）；默认 <Inbox /> */
  icon?: ReactNode;
  /** CTA 按钮文案 */
  ctaText?: string;
  /** CTA 点击 */
  onCta?: () => void;
  /** 紧凑模式（小图标 / 小字）*/
  compact?: boolean;
}

/**
 * 通用空状态 (M37, Tier 3)
 *
 * 灵感: Linear / Notion / Aion empty-state.tsx
 *   - 图标 + 标题 + 描述 + CTA 按钮
 *   - 整体居中 + 大留白
 *
 * 用法:
 *   <EmptyState
 *     title="还没有客户"
 *     description="点击右上角'新建客户'开始你的第一条记录"
 *     icon={<Building2 />}
 *     ctaText="新建客户"
 *     onCta={() => setOpen(true)}
 *   />
 */
export function EmptyState({
  title = tx('hardcoded:components.ui.emptystate.aee82bb7'),
  description,
  icon,
  ctaText,
  onCta,
  compact = false,
}: EmptyStateProps) {
  const iconSize = compact ? 36 : 56;
  const padY = compact ? 24 : 60;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${padY}px 16px`,
        textAlign: 'center',
        gap: 12,
      }}
    >
      <div
        style={{
          color: '#d1d5db',
          marginBottom: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: iconSize + 16,
          height: iconSize + 16,
          borderRadius: '50%',
          background: '#f5f7fa',
        }}
      >
        {icon ?? <Inbox size={iconSize} strokeWidth={1.4} />}
      </div>
      <Typography.Text style={{ fontSize: compact ? 14 : 15, fontWeight: 500, color: '#374151' }}>
        {title}
      </Typography.Text>
      {description && (
        <Typography.Text type="secondary" style={{ fontSize: 12, maxWidth: 360, lineHeight: 1.6 }}>
          {description}
        </Typography.Text>
      )}
      {ctaText && onCta && (
        <Button type="primary" onClick={onCta} style={{ marginTop: 8 }}>
          {ctaText}
        </Button>
      )}
    </div>
  );
}
